const MIN_LEVEL = 1;
function parseRequirement(requirement) {
    const [code, levelStr] = requirement.split(':');
    return { code, level: levelStr ? Number(levelStr) : MIN_LEVEL };
}
/**
 * Normalizes any accepted expression shape into string[][] — outer array is OR-groups, each inner
 * array is an AND-group. See the "Expression syntax → Normalized expression" table above.
 */
function normalizeRequirementExpr(expr) {
    // bare string: the whole expression is one requirement — one OR-group of one AND-group of it.
    if (typeof expr === 'string') {
        return [[expr]];
    }
    // already an array: it's already the top-level OR-list. Just make sure every entry is an
    // AND-group — a bare string entry becomes a single-item AND-group, an array entry is kept as-is.
    return expr.map((entry) => (typeof entry === 'string' ? [entry] : entry));
}
/**
 * Checks whether requiredPermissionExpr can be satisfied by the user's grantedPermissions — returns
 * true if satisfied, false otherwise. The evaluator both permission and role checks are built on,
 * named for its dominant use case: grantedPermissions is normally session.permissions ({code, access}
 * — already this exact shape, no adapting needed). `hasRole` below reuses it too, by adapting role
 * strings into the same {code, access?} shape.
 */
export function hasPermission(grantedPermissions, requiredPermissionExpr) {
    if (!grantedPermissions || grantedPermissions.length === 0)
        return false;
    const groups = normalizeRequirementExpr(requiredPermissionExpr);
    // check permission expr.
    return (
    // outer groups - some = OR
    groups.some((group) => 
    // inner groups - every = AND
    group.every((requirement) => {
        const { code, level } = parseRequirement(requirement);
        return grantedPermissions.some((item) => item.code === code && (item.access === undefined || item.access >= level));
    })));
}
/**
 * Checks whether requiredRoleExpr can be satisfied by the user's grantedRoles — returns true if
 * satisfied, false otherwise. Wraps the role(s) into {code} items (no access field — presence is the
 * whole answer) and delegates to hasPermission.
 */
export function hasRole(grantedRoles, requiredRoleExpr) {
    const roles = Array.isArray(grantedRoles) ? grantedRoles : [grantedRoles];
    return hasPermission(roles.map((code) => ({ code })), requiredRoleExpr);
}
